/**
 * Riads Page - Central Tenant Management
 * 
 * Displays all riads with comprehensive information and quick actions.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plus, Search, MoreVertical, RefreshCw,
  Eye, Play, Pause, CheckCircle, AlertTriangle, Clock,
  Users, CreditCard, Settings, ExternalLink, Edit, XCircle
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { api } from '../../App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const statusColors = {
  ACTIVE: 'bg-green-100 text-green-800',
  SUSPENDED: 'bg-orange-100 text-orange-800',
  DISABLED: 'bg-red-100 text-red-800'
};

const setupStateColors = {
  PENDING: 'bg-gray-100 text-gray-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800'
};

const subscriptionStatusColors = {
  ACTIVE: 'bg-green-100 text-green-800',
  TRIAL: 'bg-blue-100 text-blue-800',
  EXPIRED: 'bg-red-100 text-red-800',
  SUSPENDED: 'bg-orange-100 text-orange-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
  LIFETIME: 'bg-purple-100 text-purple-800'
};

const RiadsPage = () => {
  const navigate = useNavigate();
  const [riads, setRiads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [setupFilter, setSetupFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Dialogs
  const [suspendDialog, setSuspendDialog] = useState({ open: false, riad: null });
  const [reactivateDialog, setReactivateDialog] = useState({ open: false, riad: null });
  const [openTenantDialog, setOpenTenantDialog] = useState({ open: false, riad: null });
  const [suspendReason, setSuspendReason] = useState('');
  const [showCreateRiadDialog, setShowCreateRiadDialog] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetchRiads();
    fetchAccounts();
    fetchPlans();
  }, [statusFilter, setupFilter, pagination.page]);

  const fetchAccounts = async () => {
    try {
      const { data } = await api.get('/admin/customers?limit=100');
      setAccounts(data.accounts || []);
    } catch (error) {
      console.error('Error loading accounts');
    }
  };

  const fetchPlans = async () => {
    try {
      const { data } = await api.get('/admin/plans?status=ACTIVE');
      setPlans(data.plans || []);
    } catch (error) {
      console.error('Error loading plans');
    }
  };

  const fetchRiads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', 20);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);

      const { data } = await api.get(`/admin/riads?${params}`);
      
      // Filter by setup state client-side if needed
      let filteredRiads = data.riads;
      if (setupFilter !== 'all') {
        filteredRiads = filteredRiads.filter(r => r.setup_state === setupFilter);
      }
      
      setRiads(filteredRiads);
      setPagination(data.pagination);
    } catch (error) {
      toast.error('Erreur lors du chargement des riads');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(p => ({ ...p, page: 1 }));
    fetchRiads();
  };

  const handleSuspend = async () => {
    try {
      await api.post(`/admin/riads/${suspendDialog.riad.id}/suspend`, {
        reason: suspendReason
      });
      toast.success('Riad suspendu');
      setSuspendDialog({ open: false, riad: null });
      setSuspendReason('');
      fetchRiads();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suspension');
    }
  };

  const handleReactivate = async () => {
    try {
      await api.post(`/admin/riads/${reactivateDialog.riad.id}/reactivate`);
      toast.success('Riad réactivé');
      setReactivateDialog({ open: false, riad: null });
      fetchRiads();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la réactivation');
    }
  };

  const handleOpenTenant = () => {
    const riad = openTenantDialog.riad;
    if (riad) {
      // Store tenant context and navigate
      localStorage.setItem('impersonated_riad', JSON.stringify({
        id: riad.id,
        name: riad.name,
        slug: riad.slug
      }));
      
      // Log the action
      api.post('/admin/audit/log', {
        action: 'OPEN_TENANT',
        entity_type: 'Riad',
        entity_id: riad.id,
        description: `Ouverture du tenant ${riad.name}`
      }).catch(() => {});
      
      // Open in new tab
      const url = `${window.location.origin}/?tenant=${riad.id}`;
      window.open(url, '_blank');
      
      setOpenTenantDialog({ open: false, riad: null });
      toast.success(`Tenant ${riad.name} ouvert dans un nouvel onglet`);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Riads</h1>
          <p className="text-muted-foreground">Gestion centrale des tenants</p>
        </div>
        <Button onClick={() => setShowCreateRiadDialog(true)} data-testid="create-riad-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Riad
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <form onSubmit={handleSearch} className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10"
              data-testid="riads-search-input"
            />
          </div>
        </form>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="ACTIVE">Actif</SelectItem>
            <SelectItem value="SUSPENDED">Suspendu</SelectItem>
            <SelectItem value="DISABLED">Désactivé</SelectItem>
          </SelectContent>
        </Select>
        <Select value={setupFilter} onValueChange={setSetupFilter}>
          <SelectTrigger className="w-[150px]" data-testid="setup-filter">
            <SelectValue placeholder="Setup" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous setups</SelectItem>
            <SelectItem value="PENDING">En attente</SelectItem>
            <SelectItem value="IN_PROGRESS">En cours</SelectItem>
            <SelectItem value="COMPLETED">Terminé</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={fetchRiads}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{riads.filter(r => r.status === 'ACTIVE').length}</p>
              <p className="text-sm text-muted-foreground">Actifs</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{riads.filter(r => r.status === 'SUSPENDED').length}</p>
              <p className="text-sm text-muted-foreground">Suspendus</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{riads.filter(r => r.setup_state !== 'COMPLETED').length}</p>
              <p className="text-sm text-muted-foreground">Setup incomplet</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{pagination.total}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Riads Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Riad</TableHead>
                <TableHead>Client SaaS</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Setup</TableHead>
                <TableHead>Config</TableHead>
                <TableHead>Utilisateurs</TableHead>
                <TableHead>Expiration</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {riads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                    Aucun riad trouvé
                  </TableCell>
                </TableRow>
              ) : (
                riads.map(riad => (
                  <TableRow 
                    key={riad.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/admin/riads/${riad.id}`)}
                    data-testid={`riad-row-${riad.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {riad.logo ? (
                          <img 
                            src={riad.logo} 
                            alt={riad.name} 
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-violet-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{riad.name}</p>
                          <p className="text-xs text-muted-foreground">{riad.email || riad.slug}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {riad.ownerAccount ? (
                        <span 
                          className="text-violet-600 hover:underline cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/customers/${riad.account_id}`);
                          }}
                        >
                          {riad.ownerAccount.name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {riad.subscription?.planDetails ? (
                        <Badge 
                          variant="outline"
                          style={{ 
                            borderColor: riad.subscription.planDetails.color,
                            color: riad.subscription.planDetails.color
                          }}
                        >
                          {riad.subscription.planDetails.name}
                        </Badge>
                      ) : riad.subscription?.plan ? (
                        <Badge variant="secondary">{riad.subscription.plan}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[riad.status]}>
                        {riad.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={setupStateColors[riad.setup_state]}>
                        {riad.setup_state}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {riad.config_status?.status === 'OK' ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                          OK
                        </Badge>
                      ) : riad.config_status?.status === 'INCOMPLETE' ? (
                        <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
                          {riad.config_status.issues_count} issues
                        </Badge>
                      ) : (
                        <Badge variant="secondary">-</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span>{riad.userCount || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {riad.subscription?.end_date ? (
                        <span className={
                          new Date(riad.subscription.end_date) < new Date() 
                            ? 'text-red-600' 
                            : new Date(riad.subscription.end_date) < new Date(Date.now() + 30*24*60*60*1000)
                              ? 'text-orange-600'
                              : ''
                        }>
                          {format(new Date(riad.subscription.end_date), 'dd/MM/yyyy')}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" data-testid={`riad-actions-${riad.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/riads/${riad.id}`);
                          }}>
                            <Eye className="w-4 h-4 mr-2" />
                            Voir fiche
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            setOpenTenantDialog({ open: true, riad });
                          }}>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Ouvrir tenant
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {riad.status === 'ACTIVE' ? (
                            <DropdownMenuItem 
                              className="text-orange-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSuspendDialog({ open: true, riad });
                              }}
                            >
                              <Pause className="w-4 h-4 mr-2" />
                              Suspendre
                            </DropdownMenuItem>
                          ) : riad.status === 'SUSPENDED' ? (
                            <DropdownMenuItem 
                              className="text-green-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReactivateDialog({ open: true, riad });
                              }}
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Réactiver
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/riads/${riad.id}?tab=users`);
                          }}>
                            <Users className="w-4 h-4 mr-2" />
                            Utilisateurs
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/riads/${riad.id}?tab=subscription`);
                          }}>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Abonnement
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/riads/${riad.id}?tab=edit`);
                          }}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={pagination.page === 1}
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
          >
            Précédent
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {pagination.page} / {pagination.pages}
          </span>
          <Button
            variant="outline"
            disabled={pagination.page === pagination.pages}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            Suivant
          </Button>
        </div>
      )}

      {/* Suspend Dialog */}
      <Dialog open={suspendDialog.open} onOpenChange={(open) => setSuspendDialog({ open, riad: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspendre le riad</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir suspendre <strong>{suspendDialog.riad?.name}</strong> ?
              Les utilisateurs ne pourront plus accéder à ce tenant.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Raison de la suspension</label>
            <Input
              placeholder="Ex: Impayé, violation des CGU..."
              value={suspendReason}
              onChange={e => setSuspendReason(e.target.value)}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialog({ open: false, riad: null })}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleSuspend}>
              Suspendre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate Dialog */}
      <AlertDialog open={reactivateDialog.open} onOpenChange={(open) => setReactivateDialog({ open, riad: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Réactiver le riad</AlertDialogTitle>
            <AlertDialogDescription>
              Voulez-vous réactiver <strong>{reactivateDialog.riad?.name}</strong> ?
              Les utilisateurs pourront à nouveau accéder à ce tenant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleReactivate}>
              Réactiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Open Tenant Dialog */}
      <AlertDialog open={openTenantDialog.open} onOpenChange={(open) => setOpenTenantDialog({ open, riad: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-violet-600" />
              Ouvrir le tenant
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Vous allez ouvrir le tenant <strong>{openTenantDialog.riad?.name}</strong> dans un nouvel onglet.</p>
              <p className="text-orange-600 font-medium">
                Cette action sera enregistrée dans le journal d'audit.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleOpenTenant} className="bg-violet-600 hover:bg-violet-700">
              Ouvrir le tenant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Riad Dialog */}
      <CreateRiadDialog
        open={showCreateRiadDialog}
        onOpenChange={setShowCreateRiadDialog}
        accounts={accounts}
        plans={plans}
        onSuccess={() => {
          setShowCreateRiadDialog(false);
          fetchRiads();
        }}
      />
    </div>
  );
};

// Create Riad Dialog Component
const CreateRiadDialog = ({ open, onOpenChange, accounts, plans, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    address: '',
    currency: 'MAD',
    account_id: '',
    plan_id: '',
    subscription_status: 'ACTIVE',
    trial_days: 14,
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        account_id: formData.account_id ? parseInt(formData.account_id) : null,
        plan_id: formData.plan_id ? parseInt(formData.plan_id) : null,
        trial_days: formData.subscription_status === 'TRIAL' ? (parseInt(formData.trial_days) || 14) : 0
      };

      // Remove empty admin fields if no admin email
      if (!formData.admin_email) {
        delete payload.admin_email;
        delete payload.admin_first_name;
        delete payload.admin_last_name;
        delete payload.admin_password;
      }

      await api.post('/admin/riads', payload);
      toast.success('Riad créé avec succès');
      onSuccess();
      // Reset form
      setFormData({
        name: '', email: '', phone: '', city: '', address: '',
        currency: 'MAD', account_id: '', plan_id: '', subscription_status: 'ACTIVE',
        trial_days: 14, admin_email: '', admin_first_name: '',
        admin_last_name: '', admin_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouveau Riad</DialogTitle>
          <DialogDescription>
            Créez un nouveau riad (tenant) et rattachez-le à un client SaaS
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client SaaS Selection */}
          <div className="space-y-4 p-4 bg-violet-50 rounded-lg border border-violet-200">
            <h4 className="font-medium text-sm text-violet-900">Client SaaS</h4>
            <div>
              <Label>Rattacher à un client *</Label>
              <Select 
                value={formData.account_id} 
                onValueChange={v => setFormData({ ...formData, account_id: v })}
              >
                <SelectTrigger data-testid="account-select">
                  <SelectValue placeholder="Sélectionner un client" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id.toString()}>
                      {acc.name} ({acc.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Le riad sera rattaché à ce compte client
              </p>
            </div>
          </div>

          {/* Riad Information */}
          <div className="space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground">Informations du Riad</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nom du riad *</Label>
                <Input
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Riad Dar Marrakech"
                  required
                  data-testid="riad-name-input"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@riad.ma"
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+212 6 XX XX XX XX"
                />
              </div>
              <div>
                <Label>Ville</Label>
                <Input
                  value={formData.city}
                  onChange={e => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Marrakech"
                />
              </div>
              <div>
                <Label>Devise</Label>
                <Select value={formData.currency} onValueChange={v => setFormData({ ...formData, currency: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MAD">MAD - Dirham</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="USD">USD - Dollar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Adresse</Label>
                <Input
                  value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Derb Exemple, Médina"
                />
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium text-sm text-muted-foreground">Abonnement</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan</Label>
                <Select value={formData.plan_id || '__inherit__'} onValueChange={v => setFormData({ ...formData, plan_id: v === '__inherit__' ? '' : v })}>
                  <SelectTrigger data-testid="plan-select">
                    <SelectValue placeholder="Hériter du compte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__inherit__">Hériter du compte client</SelectItem>
                    {plans.map(plan => (
                      <SelectItem key={plan.id} value={plan.id.toString()}>
                        {plan.name} ({plan.price} {plan.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Statut</Label>
                <Select 
                  value={formData.subscription_status} 
                  onValueChange={v => setFormData({ ...formData, subscription_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Actif</SelectItem>
                    <SelectItem value="TRIAL">Période d'essai</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.subscription_status === 'TRIAL' && (
                <div>
                  <Label>Jours d'essai</Label>
                  <Input
                    type="number"
                    value={formData.trial_days}
                    onChange={e => setFormData({ ...formData, trial_days: e.target.value })}
                    placeholder="14"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Admin User (Optional) */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium text-sm text-muted-foreground">
              Administrateur (Optionnel)
            </h4>
            <p className="text-xs text-muted-foreground">
              Créez un utilisateur administrateur pour ce riad
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email admin</Label>
                <Input
                  type="email"
                  value={formData.admin_email}
                  onChange={e => setFormData({ ...formData, admin_email: e.target.value })}
                  placeholder="admin@riad.ma"
                  data-testid="admin-email-input"
                />
              </div>
              {formData.admin_email && (
                <>
                  <div>
                    <Label>Mot de passe *</Label>
                    <Input
                      type="password"
                      value={formData.admin_password}
                      onChange={e => setFormData({ ...formData, admin_password: e.target.value })}
                      placeholder="••••••••"
                      required={!!formData.admin_email}
                      data-testid="admin-password-input"
                    />
                  </div>
                  <div>
                    <Label>Prénom</Label>
                    <Input
                      value={formData.admin_first_name}
                      onChange={e => setFormData({ ...formData, admin_first_name: e.target.value })}
                      placeholder="Ahmed"
                    />
                  </div>
                  <div>
                    <Label>Nom</Label>
                    <Input
                      value={formData.admin_last_name}
                      onChange={e => setFormData({ ...formData, admin_last_name: e.target.value })}
                      placeholder="El Fassi"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading || !formData.account_id} data-testid="create-riad-submit">
              {loading && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              Créer le riad
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RiadsPage;
